import { appRoutePaths } from '../Root/navigation';

export type StoredRecentVisit = {
  hits: number;
  label: string;
  pathname: string;
  timestamp: number;
};

const RECENT_VISITS_STORAGE_KEY = 'driveline.recent-visits.v2';
const RECENT_VISITS_EVENT = 'driveline:recent-visits';
const MAX_RECENT_VISITS = 12;

const routeLabels: Record<string, string> = {
  [appRoutePaths.apis]: 'APIs',
  [appRoutePaths.catalog]: 'Catalog',
  [appRoutePaths.docs]: 'Docs',
  [appRoutePaths.groups]: 'My Groups',
  [appRoutePaths.import]: 'Import Component',
  [appRoutePaths.search]: 'Search',
  [appRoutePaths.settings]: 'Settings',
  [appRoutePaths.access]: 'Access Management',
};

const titleCase = (value: string) =>
  value
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());

export const recentVisitsChangedEvent = RECENT_VISITS_EVENT;

export const readRecentVisits = (): StoredRecentVisit[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(RECENT_VISITS_STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .filter(
        (visit): visit is StoredRecentVisit =>
          typeof visit?.pathname === 'string' &&
          typeof visit?.label === 'string' &&
          typeof visit?.timestamp === 'number' &&
          typeof visit?.hits === 'number',
      )
      .sort((a, b) => b.timestamp - a.timestamp);
  } catch {
    return [];
  }
};

const writeRecentVisits = (visits: StoredRecentVisit[]) => {
  window.localStorage.setItem(
    RECENT_VISITS_STORAGE_KEY,
    JSON.stringify(visits.slice(0, MAX_RECENT_VISITS)),
  );
  window.dispatchEvent(new CustomEvent(RECENT_VISITS_EVENT));
};

const labelFromPathname = (pathname: string) => {
  const cleanPath = pathname.split('?')[0] || '/';
  if (routeLabels[cleanPath]) {
    return routeLabels[cleanPath];
  }

  const segments = cleanPath.split('/').filter(Boolean);
  if (segments[0] === 'catalog' && segments.length >= 4) {
    return titleCase(decodeURIComponent(segments[3]));
  }
  if (segments[0] === 'docs' && segments.length >= 4) {
    return titleCase(decodeURIComponent(segments[3]));
  }

  return titleCase(decodeURIComponent(segments.at(-1) ?? 'Home'));
};

export const recordRecentVisit = (pathname: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  const cleanPath = pathname.split('?')[0] || '/';
  if (cleanPath === '/') {
    return;
  }

  const href = pathname || cleanPath;
  const visits = readRecentVisits();
  const existing = visits.find(visit => visit.pathname === href);
  const nextVisit: StoredRecentVisit = {
    hits: existing ? existing.hits + 1 : 1,
    label: labelFromPathname(cleanPath),
    pathname: href,
    timestamp: Date.now(),
  };

  writeRecentVisits([
    nextVisit,
    ...visits.filter(visit => visit.pathname !== href),
  ]);
};

export const installRecentVisitTracker = () => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const recordCurrentLocation = () => {
    window.setTimeout(() => {
      recordRecentVisit(`${window.location.pathname}${window.location.search}`);
    }, 0);
  };

  const originalPushState = window.history.pushState;
  const originalReplaceState = window.history.replaceState;

  window.history.pushState = function patchedPushState(
    this: History,
    ...args: Parameters<History['pushState']>
  ) {
    const result = originalPushState.apply(this, args);
    recordCurrentLocation();
    return result;
  } as History['pushState'];

  window.history.replaceState = function patchedReplaceState(
    this: History,
    ...args: Parameters<History['replaceState']>
  ) {
    const result = originalReplaceState.apply(this, args);
    recordCurrentLocation();
    return result;
  } as History['replaceState'];

  window.addEventListener('popstate', recordCurrentLocation);
  recordCurrentLocation();

  return () => {
    window.history.pushState = originalPushState;
    window.history.replaceState = originalReplaceState;
    window.removeEventListener('popstate', recordCurrentLocation);
  };
};
